import React, { useState } from "react";
import { Card, Descriptions, Button, Modal, Input, message, Typography, Space } from "antd";
import { DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useKeycloak } from "@react-keycloak/web";
import { useTranslation } from "react-i18next";
import { UserService } from "../services/userService";

const { Title, Text, Paragraph } = Typography;

// Lets the signed-in user view their profile and permanently delete their own
// account (parity with the mobile app). Delete is gated behind a type-to-confirm
// keyword so it can't be triggered by accident.
export default function AccountSettings() {
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();

  const [modalOpen, setModalOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const claims = keycloak?.tokenParsed || {};
  const fullName =
    [claims.given_name, claims.family_name].filter(Boolean).join(" ").trim() ||
    claims.name ||
    claims.preferred_username ||
    "—";
  const email = claims.email || "—";
  const roles = claims.realm_access?.roles || [];
  const roleLabel = roles.includes("mediadmin") ? t("roleAdmin") : t("roleUser");

  const keyword = t("accountDeleteKeyword");
  const canConfirm = confirmText.trim().toLowerCase() === keyword.toLowerCase();

  const openModal = () => {
    setConfirmText("");
    setModalOpen(true);
  };
  const closeModal = () => {
    if (deleting) return;
    setModalOpen(false);
    setConfirmText("");
  };

  const handleDelete = async () => {
    if (!canConfirm || deleting) return;
    setDeleting(true);
    try {
      await UserService.deleteOwnAccount();
      message.success(t("deleteAccountSuccess"));
      // Tear down the session and leave the app. The full-page redirect also
      // clears all client-side (Redux/local) state.
      keycloak.logout({ redirectUri: window.location.origin });
    } catch (error) {
      message.error(t("deleteAccountError"));
      setDeleting(false); // keep the user signed in so they can retry
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: "24px auto", padding: "0 16px" }}>
      <Title level={3}>{t("accountTitle")}</Title>

      <Card style={{ marginBottom: 24 }}>
        <Descriptions column={1} bordered size="middle">
          <Descriptions.Item label="Name">{fullName}</Descriptions.Item>
          <Descriptions.Item label={t("email")}>{email}</Descriptions.Item>
          <Descriptions.Item label={t("accountRole")}>{roleLabel}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={<Text type="danger">{t("deleteAccount")}</Text>}>
        <Paragraph type="secondary">{t("deleteAccountConfirmMessage")}</Paragraph>
        <Button danger type="primary" icon={<DeleteOutlined />} onClick={openModal}>
          {t("deleteAccount")}
        </Button>
      </Card>

      <Modal
        open={modalOpen}
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
            {t("deleteAccountConfirmTitle")}
          </Space>
        }
        onCancel={closeModal}
        onOk={handleDelete}
        okText={t("delete")}
        cancelText={t("cancel")}
        okButtonProps={{ danger: true, disabled: !canConfirm, loading: deleting }}
        cancelButtonProps={{ disabled: deleting }}
        maskClosable={!deleting}
        closable={!deleting}
      >
        <Paragraph>{t("deleteAccountConfirmMessage")}</Paragraph>
        <Paragraph type="secondary">{t("deleteAccountTypeToConfirm", { keyword })}</Paragraph>
        <Input
          autoFocus
          value={confirmText}
          placeholder={keyword}
          onChange={(e) => setConfirmText(e.target.value)}
          onPressEnter={handleDelete}
          disabled={deleting}
        />
      </Modal>
    </div>
  );
}
